<script setup lang="ts">
import { Activity, Bell, LayoutDashboard, LogOut, Palette, Settings2 } from '@lucide/vue'
import PopoverMenu from './PopoverMenu.vue'

defineProps<{ backTo?: string; backLabel?: string }>()
</script>
<template>
  <div class="min-h-screen">
    <header
      class="sticky top-0 z-30 border-b border-slate-200/60 bg-white/85 backdrop-blur-xl dark:border-slate-700/70 dark:bg-slate-950/85"
    >
      <div
        class="mx-auto flex min-h-16 max-w-[1500px] items-center justify-between gap-3 px-4 py-2 sm:px-6 lg:px-8"
      >
        <div class="flex min-w-0 items-center gap-3">
          <RouterLink
            to="/"
            class="group flex shrink-0 items-center gap-2.5 rounded-lg"
            :aria-label="$t('nav.dashboardAria')"
          >
            <span
              class="grid size-9 place-items-center rounded-xl bg-ink text-white transition group-hover:bg-moss-700 dark:bg-moss-700"
            >
              <svg viewBox="0 0 24 24" class="size-5" fill="none" aria-hidden="true">
                <path
                  d="M3 12h3.2l2-5.2 3.2 10.5 2.4-7.2 1.6 3.9H21"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span class="hidden font-display text-lg font-bold tracking-tight sm:block">{{
              $t('brand')
            }}</span>
          </RouterLink>
          <template v-if="backTo"
            ><span class="text-slate-300">/</span
            ><RouterLink :to="backTo" class="truncate text-sm text-slate-500 hover:text-moss-700">{{
              backLabel || $t('common.back')
            }}</RouterLink></template
          >
        </div>
        <nav class="flex shrink-0 items-center gap-1" :aria-label="$t('nav.settings')">
          <RouterLink
            to="/"
            class="nav-button"
            :title="$t('nav.dashboard')"
            :aria-label="$t('nav.dashboard')"
            ><LayoutDashboard class="size-4" aria-hidden="true" /><span class="hidden md:inline">{{
              $t('nav.dashboard')
            }}</span></RouterLink
          >
          <RouterLink
            to="/uptime"
            class="nav-button"
            :title="$t('nav.uptime')"
            :aria-label="$t('nav.uptime')"
            ><Activity class="size-4" aria-hidden="true" /><span class="hidden md:inline">{{
              $t('design.compactUptime')
            }}</span></RouterLink
          >
          <PopoverMenu
            :label="$t('nav.settings')"
            :width="230"
            button-class="nav-button"
            :icon-only="true"
          >
            <template #trigger
              ><Settings2 class="size-4" aria-hidden="true" /><span class="hidden md:inline">{{
                $t('nav.settings')
              }}</span></template
            >
            <template #default="{ close }">
              <RouterLink
                to="/settings/appearance"
                class="menu-item"
                role="menuitem"
                @click="close(false)"
                ><Palette class="size-4" aria-hidden="true" />{{ $t('nav.appearance') }}</RouterLink
              >
              <RouterLink
                to="/settings/alerts"
                class="menu-item"
                role="menuitem"
                @click="close(false)"
                ><Bell class="size-4" aria-hidden="true" />{{ $t('nav.alerts') }}</RouterLink
              >
              <RouterLink
                to="/settings/admin"
                class="menu-item"
                role="menuitem"
                @click="close(false)"
                ><Settings2 class="size-4" aria-hidden="true" />{{ $t('nav.admin') }}</RouterLink
              >
              <div class="my-1 border-t border-slate-100 dark:border-slate-700" />
              <RouterLink to="/auth/logout" class="menu-item" role="menuitem" @click="close(false)"
                ><LogOut class="size-4" aria-hidden="true" />{{ $t('nav.signOut') }}</RouterLink
              >
            </template>
          </PopoverMenu>
        </nav>
      </div>
    </header>
    <main class="mx-auto max-w-[1500px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8"><slot /></main>
    <footer class="mx-auto max-w-[1500px] px-6 pb-5 text-right text-[10px] text-slate-400">
      <a href="https://db-ip.com" target="_blank" rel="noopener noreferrer"
        >IP Geolocation by DB-IP</a
      >
    </footer>
  </div>
</template>
